import React, { useState, useEffect } from "react";
import { Form, Button, Message } from "semantic-ui-react";
import { useFormik } from "formik";
import { User, Role, Company, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { isAdmin, isMaster } from "../../../../utils/checkPermission";
import { initialValues, validationSchema } from "./InviteUserForm.form";
import { SitesMultiSelect } from "../../../Shared";

const userController = new User();
const roleController = new Role();
const companyController = new Company();
const siteController = new Site();

export function InviteUserForm({ close, onReload }) {
  const { accessToken, user: { role, company } } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [listRoles, setListRoles] = useState([]);
  const [listCompanies, setListCompanies] = useState([]);
  const [listSites, setListSites] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    roleController.getRoles(accessToken, true).then(setListRoles);

    if (isMaster(role)) {
      companyController.getCompanies(accessToken, true).then(setListCompanies);
    } else if (isAdmin(role) && company) {
      siteController.getSitesByCompany(accessToken, company, true).then(setListSites);
    }
  }, []);

  const formik = useFormik({
    initialValues: initialValues(),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        setError("");
        setSuccessMsg("");

        const data = {
          email: formValue.email,
          role: formValue.role,
          sites: formValue.sites || [],
          company: isMaster(role) ? formValue.company || undefined : undefined,
        };

        await userController.inviteUser(accessToken, data);
        setSuccessMsg(`${t("invitation_sent")} ${formValue.email}`);
        formik.resetForm();

        setTimeout(() => {
          onReload && onReload();
          close && close();
        }, 1800);
      } catch (err) {
        setError(err.msg || t("error_sending_invitation"));
      }
    },
  });

  // Cuando el master cambia de empresa, recargar sitios
  useEffect(() => {
    if (isMaster(role) && formik.values.company) {
      siteController
        .getSitesByCompany(accessToken, formik.values.company, true)
        .then(setListSites);
    }
  }, [formik.values.company]);

  return (
    <Form className="invite-user-form" onSubmit={formik.handleSubmit} style={{ padding: "1rem" }}>
      {successMsg && <Message positive content={successMsg} />}
      {error && <Message negative content={error} />}

      <Form.Input
        label={<label><span style={{ color: "red", marginRight: 4 }}>*</span>{t("email")}</label>}
        name="email"
        type="email"
        placeholder={t("email")}
        onChange={formik.handleChange}
        value={formik.values.email}
        error={formik.errors.email}
      />

      <Form.Dropdown
        label={<label><span style={{ color: "red", marginRight: 4 }}>*</span>{t("role")}</label>}
        placeholder={t("select")}
        options={listRoles.map((r) => ({ key: r._id, text: r.name, value: r._id }))}
        selection
        onChange={(_, data) => formik.setFieldValue("role", data.value)}
        value={formik.values.role}
        error={!!formik.errors.role}
      />

      {isMaster(role) && (
        <Form.Dropdown
          label={t("company")}
          placeholder={t("select")}
          options={listCompanies.map((c) => ({ key: c._id, text: c.name, value: c._id }))}
          selection
          onChange={(_, data) => {
            formik.setFieldValue("company", data.value);
            formik.setFieldValue("sites", []);
          }}
          value={formik.values.company}
        />
      )}

      {/* ADMINISTRADOR tiene acceso a todos los sitios: no se asigna sitio específico */}
      {!listRoles.find(r => r._id === formik.values.role &&
        ["admin", "administrador"].includes(r.name?.toLowerCase())) && (
        <Form.Field error={!!formik.errors.sites}>
          <label>{t("site")}</label>
          <SitesMultiSelect
            sites={listSites}
            value={formik.values.sites}
            onChange={(v) => formik.setFieldValue("sites", v)}
            error={formik.errors.sites}
            t={t}
          />
        </Form.Field>
      )}

      <div style={{ display: "flex", gap: "1rem", marginTop: "36px", justifyContent: "center" }}>
        <Button type="submit" primary loading={formik.isSubmitting}>
          {t("invite_user")}
        </Button>
        <Button type="button" secondary onClick={close}>
          {t("cancel")}
        </Button>
      </div>
    </Form>
  );
}
